import React, { useState, useEffect } from 'react';
import { api, formatDate } from '../api';

const SHIFTS = ['pagi', 'siang', 'malam'];
const SHIFT_TIME = { pagi: '06:00 - 14:00', siang: '14:00 - 22:00', malam: '22:00 - 06:00' };
const SHIFT_COLOR = { pagi: 'var(--yellow)', siang: 'var(--blue)', malam: 'var(--navy-400)' };

export default function JadwalPage({ user }) {
  const [jadwal, setJadwal] = useState([]);
  const [wilayah, setWilayah] = useState([]);
  const [anggota, setAnggota] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [filter, setFilter] = useState({ bulan: new Date().getMonth() + 1, tahun: new Date().getFullYear(), wilayah_id: '' });
  const [form, setForm] = useState({ anggota_id: '', wilayah_id: '', tanggal: '', shift: 'pagi', status: 'aktif' });
  const [reqForm, setReqForm] = useState({ tanggal_request: '', shift_request: 'pagi', alasan: '' });
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, [filter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ bulan: filter.bulan, tahun: filter.tahun, ...(filter.wilayah_id && { wilayah_id: filter.wilayah_id }) });
      const [j, w] = await Promise.all([
        api.get(`/jadwal?${params}`),
        api.get('/wilayah'),
      ]);
      setJadwal(j);
      setWilayah(w);
      if (user.role !== 'anggota') {
        const a = await api.get('/users');
        setAnggota(a.filter(u => u.role === 'anggota'));
      }
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setLoading(false); }
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3000);
  };

  const saveJadwal = async () => {
    if (!form.anggota_id || !form.wilayah_id || !form.tanggal || !form.shift) {
      showMsg('Lengkapi semua field', 'error'); return;
    }
    setSaving(true);
    try {
      await api.post('/jadwal', form);
      showMsg('Jadwal berhasil ditambahkan');
      setShowForm(false);
      setForm({ anggota_id: '', wilayah_id: '', tanggal: '', shift: 'pagi', status: 'aktif' });
      fetchAll();
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const sendRequest = async () => {
    if (!reqForm.tanggal_request || !reqForm.alasan) {
      showMsg('Tanggal dan alasan wajib diisi', 'error'); return;
    }
    setSaving(true);
    try {
      await api.post('/jadwal/request', reqForm);
      showMsg('Request jadwal terkirim');
      setShowRequest(false);
      setReqForm({ tanggal_request: '', shift_request: 'pagi', alasan: '' });
    } catch (err) { showMsg(err.message, 'error'); }
    finally { setSaving(false); }
  };

  const deleteJadwal = async (id) => {
    if (!window.confirm('Hapus jadwal ini?')) return;
    try {
      await api.delete(`/jadwal/${id}`);
      showMsg('Jadwal dihapus');
      fetchAll();
    } catch (err) { showMsg(err.message, 'error'); }
  };

  const bulanList = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2024, i).toLocaleDateString('id-ID', { month: 'long' })
  }));

  const groupedByDate = jadwal.reduce((acc, j) => {
    const d = j.tanggal?.split('T')[0] || j.tanggal;
    if (!acc[d]) acc[d] = [];
    acc[d].push(j);
    return acc;
  }, {});

  return (
    <div className="page fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 className="page-title">Jadwal</h1>
          <p className="page-subtitle">Jadwal kerja anggota</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {user.role === 'anggota' && (
            <button className="btn btn-outline btn-sm" onClick={() => setShowRequest(true)}>+ Request</button>
          )}
          {(user.role === 'admin' || user.role === 'spv') && (
            <button className="btn btn-gold btn-sm" onClick={() => setShowForm(true)}>+ Jadwal</button>
          )}
        </div>
      </div>

      {msg.text && <div className={`toast toast-${msg.type}`}>{msg.text}</div>}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <select className="input" style={{ flex: 1, minWidth: 120 }} value={filter.bulan} onChange={e => setFilter(f => ({ ...f, bulan: e.target.value }))}>
          {bulanList.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
        <select className="input" style={{ width: 90 }} value={filter.tahun} onChange={e => setFilter(f => ({ ...f, tahun: e.target.value }))}>
          {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {user.role !== 'anggota' && wilayah.length > 0 && (
          <select className="input" style={{ flex: 1, minWidth: 120 }} value={filter.wilayah_id} onChange={e => setFilter(f => ({ ...f, wilayah_id: e.target.value }))}>
            <option value="">Semua Wilayah</option>
            {wilayah.map(w => <option key={w.id} value={w.id}>{w.nama}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div className="loading-center"><div className="loading-spinner" /></div>
      ) : Object.keys(groupedByDate).length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <p>Belum ada jadwal bulan ini</p>
        </div>
      ) : (
        Object.entries(groupedByDate).sort(([a], [b]) => a.localeCompare(b)).map(([date, items]) => (
          <div key={date} className="section">
            <div className="section-header">
              <span className="section-title">
                {new Date(date + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
            </div>
            {items.map(j => (
              <div key={j.id} className="list-item">
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--navy-600)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                  {j.shift === 'pagi' ? '🌅' : j.shift === 'siang' ? '☀️' : '🌙'}
                </div>
                <div className="list-item-content">
                  {user.role !== 'anggota' && <div className="list-item-title">{j.anggota_name}</div>}
                  <div className="list-item-sub" style={{ color: SHIFT_COLOR[j.shift] }}>
                    {j.shift.charAt(0).toUpperCase() + j.shift.slice(1)} · {SHIFT_TIME[j.shift]}
                  </div>
                  {user.role !== 'anggota' && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{j.wilayah_nama}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <span className={`badge ${j.status === 'aktif' ? 'badge-green' : j.status === 'libur' ? 'badge-gray' : 'badge-yellow'}`}>
                    {j.status}
                  </span>
                  {(user.role === 'admin' || user.role === 'spv') && (
                    <button className="btn btn-ghost btn-sm" style={{ padding: '2px 8px', fontSize: '0.7rem', color: 'var(--red)' }} onClick={() => deleteJadwal(j.id)}>
                      Hapus
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))
      )}

      {/* Modal Tambah Jadwal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 className="modal-title">Tambah Jadwal</h2>
            <div className="input-group">
              <label className="input-label">Anggota</label>
              <select className="input" value={form.anggota_id} onChange={e => setForm(f => ({ ...f, anggota_id: e.target.value }))}>
                <option value="">Pilih anggota...</option>
                {anggota.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Wilayah</label>
              <select className="input" value={form.wilayah_id} onChange={e => setForm(f => ({ ...f, wilayah_id: e.target.value }))}>
                <option value="">Pilih wilayah...</option>
                {wilayah.map(w => <option key={w.id} value={w.id}>{w.nama}</option>)}
              </select>
            </div>
            <div className="grid-2">
              <div className="input-group">
                <label className="input-label">Tanggal</label>
                <input type="date" className="input" value={form.tanggal} onChange={e => setForm(f => ({ ...f, tanggal: e.target.value }))} />
              </div>
              <div className="input-group">
                <label className="input-label">Shift</label>
                <select className="input" value={form.shift} onChange={e => setForm(f => ({ ...f, shift: e.target.value }))}>
                  {SHIFTS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)} · {SHIFT_TIME[s]}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost btn-full" onClick={() => setShowForm(false)}>Batal</button>
              <button className="btn btn-gold btn-full" onClick={saveJadwal} disabled={saving}>
                {saving ? '...' : 'Simpan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Request Jadwal */}
      {showRequest && (
        <div className="modal-overlay" onClick={() => setShowRequest(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 className="modal-title">Request Perubahan Jadwal</h2>
            <div className="grid-2">
              <div className="input-group">
                <label className="input-label">Tanggal</label>
                <input type="date" className="input" value={reqForm.tanggal_request} onChange={e => setReqForm(f => ({ ...f, tanggal_request: e.target.value }))} />
              </div>
              <div className="input-group">
                <label className="input-label">Shift</label>
                <select className="input" value={reqForm.shift_request} onChange={e => setReqForm(f => ({ ...f, shift_request: e.target.value }))}>
                  {SHIFTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">Alasan</label>
              <textarea className="input" rows={3} placeholder="Jelaskan alasan request perubahan jadwal..." value={reqForm.alasan} onChange={e => setReqForm(f => ({ ...f, alasan: e.target.value }))} style={{ resize: 'none' }} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost btn-full" onClick={() => setShowRequest(false)}>Batal</button>
              <button className="btn btn-gold btn-full" onClick={sendRequest} disabled={saving}>
                {saving ? '...' : 'Kirim Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
